import { plural } from '@cian/utils';
import { numberToPrettyString } from '@cian/utils/lib/shared/number_helpers';
import { useSelector } from 'react-redux';

import { IOfferSummaryInfoItemProps } from '../../../../../components/OfferSummaryInfo';
import { DecorationType, WindowsViewType } from '../../../../../components/additional_features/locales';
import { offerSelector } from '../../../../../selectors';
import { FDealType, getDealType } from '../../../../../utils/deal_type';
import { FOfferType, getOfferType, isUrban } from '../../../../../utils/offer_type';

export const useSummaryInfoUrbanProps = (): IOfferSummaryInfoItemProps[] | null => {
  const offer = useSelector(offerSelector);

  const dealType = getDealType(offer);
  const offerType = getOfferType(offer);

  const isUrbanOffer = isUrban(offerType);

  if (!isUrbanOffer) {
    return null;
  }

  const { loggiasCount, balconiesCount, combinedWcsCount, separateWcsCount, isApartments } = offer;

  const shouldRenderOfferType = dealType === FDealType.Sale;

  const shouldRenderStatus = Boolean(isApartments || offer.isPenthouse);

  const pluralasedCombinedWcsValue = combinedWcsCount
    ? `${combinedWcsCount} ${plural(combinedWcsCount, ['совмещенный', 'совмещенных', 'совмещенных'])}`
    : null;
  const pluralasedSeparateWcsValue = separateWcsCount
    ? `${separateWcsCount} ${plural(separateWcsCount, ['раздельный', 'раздельных', 'раздельных'])}`
    : null;
  const wcsValue = [pluralasedCombinedWcsValue, pluralasedSeparateWcsValue].filter(Boolean).join(', ');

  const pluralasedBalconiesValue = balconiesCount
    ? `${balconiesCount} ${plural(balconiesCount, ['балкон', 'балкона', 'балконов'])}`
    : null;
  const pluralasedLoggiasValue = loggiasCount
    ? `${loggiasCount} ${plural(loggiasCount, ['лоджия', 'лоджии', 'лоджий'])}`
    : null;
  const balconyOrLoggiasValue = [pluralasedBalconiesValue, pluralasedLoggiasValue].filter(Boolean).join(', ');

  const summaryInfoItems: IOfferSummaryInfoItemProps[] = [];

  if (shouldRenderOfferType || shouldRenderStatus) {
    const offerTypeValue = shouldRenderOfferType
      ? offerType === FOfferType.FlatNew
        ? 'Новостройка'
        : 'Вторичка'
      : null;
    const statusValue = shouldRenderStatus ? (isApartments ? 'Апартаменты' : 'Пентхаус') : null;

    const typeValue = [offerTypeValue, statusValue].filter(Boolean).join(' ');

    summaryInfoItems.push({
      name: 'Тип жилья',
      value: typeValue,
    });
  }

  if (offer.totalArea) {
    summaryInfoItems.push({
      name: 'Общая площадь',
      value: offer.totalArea,
    });
  }

  if (offer.livingArea) {
    summaryInfoItems.push({
      name: 'Жилая площадь',
      value: offer.livingArea,
    });
  }

  if (offer.kitchenArea) {
    summaryInfoItems.push({
      name: 'Площадь кухни',
      value: offer.kitchenArea,
    });
  }

  if (offer.roomArea) {
    summaryInfoItems.push({
      name: 'Площадь комнат',
      value: `${numberToPrettyString(Number(offer.roomArea))} м\u00B2`,
    });
  }

  if (offer.building && Boolean(offer.building.ceilingHeight)) {
    summaryInfoItems.push({
      name: 'Высота потолков',
      value: `${numberToPrettyString(Number(offer.building.ceilingHeight))} м`,
    });
  }

  if (offer.floorFrom) {
    summaryInfoItems.push({
      name: 'Этаж',
      value: offer.floorFrom,
    });
  }

  if (wcsValue) {
    summaryInfoItems.push({
      name: 'Санузел',
      value: wcsValue,
    });
  }

  if (balconyOrLoggiasValue) {
    summaryInfoItems.push({
      name: 'Балкон/лоджия',
      value: balconyOrLoggiasValue,
    });
  }

  if (offer.windowsViewType) {
    summaryInfoItems.push({
      name: 'Вид из окон',
      value: WindowsViewType[offer.windowsViewType],
    });
  }

  if (!shouldRenderOfferType) {
    summaryInfoItems.push({
      name: 'Можно с детьми',
      value: offer.childrenAllowed ? 'Да' : 'Нет',
    });

    summaryInfoItems.push({
      name: 'Можно с животными',
      value: offer.petsAllowed ? 'Да' : 'Нет',
    });
  }

  if (offer.bedsCount) {
    summaryInfoItems.push({
      name: 'Количество спальных мест',
      value: Number(offer.bedsCount),
    });
  }

  if (offer.decoration) {
    summaryInfoItems.push({
      name: 'Отделка',
      value: DecorationType[offer.decoration],
    });
  }

  return summaryInfoItems;
};
